/**
 * FALL RENDERER — stateless-in-gameplay 2D renderer for the fall stage, in the cartoon style
 * (docs/CARTOON_ART.md).
 *
 * No runway background, no perspective math. Each frame receives:
 *   - ctx, runWidth, runHeight
 *   - bodiesWithState: [{obj, body, suction}] from FallPhysics
 *   - player for flask visual position and shield state
 *   - currentTime (sim clock: spawn pop, suction) and animTime (pause-aware loop clock)
 *   - state, countdownRemaining
 *
 * Draw order:
 *   1. Movement limit buoys in the sea (flask stop points; bob and tilt)
 *   1b. Caught creatures being swallowed (run/fall-swallow.js): drawn behind the flask glass,
 *      so once through the mouth they read as inside it
 *   2. Player flask (squashes on boulder hits and gulps on catches, white hit flash)
 *   3. Fall collider debug overlay (`drawFallColliderDebug` when `runtimeFlags.showFallColliderDebug`)
 *   4. Live physics-positioned objects (regular + debris fragments), by body.position: cartoon
 *      pop on spawn (with a juice poof), stretch along the velocity, speed lines when fast;
 *      creatures are layered (disc and shine upright so their light stays upper left, the
 *      creature turning with the body, blinking now and then)
 *   5. Bubble shield (pops in, wobbles, swells and bursts on a block)
 *   6. Shard bursts, then the countdown digit, then the Fall callout (inked lettering)
 *
 * Presentation only: it reads physics and player state and never writes it. Effects that
 * outlive a frame (puffs, sparkles, rings) go to the juice engine (`this.juice`, set by the
 * game host; see render/juice). Pictures are baked once (render/toon/fall/fall-fx-art.js) and
 * only transformed per frame.
 *
 * The sky/sea background is drawn separately by the game host (`onDrawSky`), not here.
 */
import * as C from "../config/constants.js";
import { computeFallDrawTopOverflowPx, fallObjectPixelWidth } from "../config/constants.js";
import { fallCountdownFontSizePx, readMockupScaleSideFromDom } from "../layout/mockup-ui-scale.js";
import { FlaskRenderer } from "../flask/flask-renderer.js";
import { areRuntimeParticlesEnabled } from "../core/runtime-flags.js";
import { drawMergeParticles } from "./merge-particles.js";
import {
  fallMotionTrailIntensity,
  isFallMotionTrailEnabled,
  layoutFallSpeedLines,
} from "../render/fall-motion-trail.js";
import { prefersReducedMotion } from "../core/sky-background-prefs.js";
import { drawFallMovementRange, collectFallBuoySpriteSpecs } from "./fall-movement-range.js";
import { drawFallColliderDebug } from "./fall-collect-slab.js";
import {
  FALL_CALLOUT_ANCHOR_Y,
  computeFallCalloutFontPx,
  getAllFallCalloutTexts,
  getFallCalloutColor,
  getFallCalloutPresentation,
} from "./fall-callouts.js";
import { computeScorePopupStrokeWidth } from "./score-popups.js";
import { MergeAssets } from "../assets/merge-assets.js";
import { releaseCanvasTexture } from "../render/toon/jar/texture-hints.js";
import { INK } from "../render/toon/palette.js";
import {
  getFallFxArt,
  SHIELD_BUBBLE_FRAME,
  TELEGRAPH_RING_FRAME,
  WARN_MARKER_FRAME,
} from "../render/toon/fall/fall-fx-art.js";
import { getFallLettering } from "../render/toon/fall/fall-lettering.js";
import {
  FALL_POP_DURATION_MUL,
  catcherFlaskSquash,
  countdownDigitPop,
  fallAppearPop,
  fallBlinkClosed,
  fallVelocityStretch,
  shieldBubbleLook,
  stretchAxes,
  warnMarkerHop,
} from "../render/toon/fall/fall-look.js";
import { getInkTextSprite } from "../render/juice/juice-text.js";
import { SWALLOW_FRAME, swallowFrame, swallowPose, swallowRest } from "./fall-swallow.js";
import { tierColor } from "../render/juice/juice-palettes.js";

var TAU = Math.PI * 2;

/** Countdown digit colours (traffic-light count: 3 coral, 2 gold, 1 green). */
var COUNTDOWN_COLORS = { 3: "#ff7a5c", 2: "#ffc84a", 1: "#5fd068" };

/** Extra length at full speed, per falling type (rocks barely give, creatures are rubbery). */
var STRETCH_BY_TYPE = { merge_pickup: 0.09, gem: 0.08, bonus: 0.07, boulder: 0.04 };

/**
 * Which falling types get speed lines. Boulders leave a dust trail instead (juice auto trails,
 * render/juice/juice-moments.js fallTrailFor), so no object gets both.
 */
var SPEED_LINE_TYPES = { merge_pickup: true, gem: true, bonus: true };

/** How long a flask hit flashes white (ms). */
var FLASK_HIT_FLASH_MS = 150;

/** Juice spring kicked when something is swallowed (read back as a small gulp squash). */
var GULP_SPRING = "flaskGulp";

var HAZARD_SIDES = ["left", "right"];

/** How often the stylesheet `--scale-side` fallback is re-read (ms). */
var SCALE_SIDE_REREAD_MS = 500;

function clamp(v, a, b) {
  return v < a ? a : v > b ? b : v;
}

/** `--scale-side` from <html>'s inline style (no style recalc), or null when not set. */
function readInlineScaleSide() {
  if (typeof document === "undefined") return null;
  var root = document.documentElement;
  if (!root || !root.style || typeof root.style.getPropertyValue !== "function") return null;
  var n = parseFloat(root.style.getPropertyValue("--scale-side"));
  return isFinite(n) && n > 0 ? n : null;
}

/**
 * GPU sprite size matching each object's Canvas 2D `draw()` (gem square, shield letterbox).
 * @param {{ getType?: function, getAspectRatio?: function }} obj
 * @param {number} pixelW
 * @param {number} pixelH
 * @returns {{ width: number, height: number }}
 */
export function fallObjectSpriteSize(obj, pixelW, pixelH) {
  var w = pixelW > 0 ? pixelW : 0;
  var h = pixelH > 0 ? pixelH : 0;
  var type = obj && typeof obj.getType === "function" ? obj.getType() : "";
  if (type === "gem") {
    var size = Math.min(w, h);
    return { width: size, height: size };
  }
  if (type === "bonus") {
    var aspect = obj && typeof obj.getAspectRatio === "function" ? obj.getAspectRatio() : 1;
    if (!(aspect > 0)) aspect = 1;
    var drawW = w;
    var drawH = w / aspect;
    if (drawH > h) {
      drawH = h;
      drawW = h * aspect;
    }
    return { width: drawW, height: drawH };
  }
  return { width: w, height: h };
}

/** Last layer record per tier (rebuilt when a layer is baked or re-baked). */
var creatureLayerMemo = Object.create(null);

/**
 * Baked layers of a creature, shared with the Merge jar (assets/merge-assets.js): disc and
 * shine are drawn upright, the creature (or its blink) turns with the body. The glint is the
 * one shared picture (`tierShine/cell`), whose frame differs from some tiers' by a few px, so
 * each layer comes with its logical frame and is sized from that (`frame` is the tier's).
 *
 * Never draws: layers not baked yet are queued for idle time and this returns null (the caller
 * draws the composite picture for those frames); a blink not baked yet is the open-eyed body.
 * Null when not drawable (tests).
 * @param {number} tier
 * @returns {{
 *   disc: HTMLCanvasElement, body: HTMLCanvasElement, blink: HTMLCanvasElement,
 *   shine: HTMLCanvasElement|null, frame: { w: number, h: number }, shineFrame: { w: number, h: number }|null
 * }|null}
 */
export function getFallCreatureLayers(tier) {
  if (!MergeAssets || typeof MergeAssets.peekLayer !== "function") return null;
  var disc = MergeAssets.peekLayer(tier, "disc");
  var body = MergeAssets.peekLayer(tier, "body");
  var blink = MergeAssets.peekLayer(tier, "blink") || body;
  var shine = MergeAssets.peekLayer(tier, "shine");
  if (!disc || !body) return null;
  // Same record while the bakes are the same (no allocation per creature per frame).
  var memo = creatureLayerMemo[tier];
  if (memo && memo.disc === disc && memo.body === body && memo.blink === blink && memo.shine === shine) {
    return memo;
  }
  var frame = MergeAssets.frame(tier);
  if (!frame) return null;
  memo = {
    disc: disc,
    body: body,
    blink: blink,
    shine: shine,
    frame: frame,
    shineFrame: shine ? MergeAssets.frame(tier, "shine") : null,
  };
  creatureLayerMemo[tier] = memo;
  return memo;
}

var compositeFreed = Object.create(null);

/**
 * Once a tier draws as layers, its composite picture (shown while they were baking) is not
 * drawn again: free its GPU texture (a no-op when it was never uploaded, or on Canvas 2D).
 */
function freeCompositeTexture(tier, image) {
  if (compositeFreed[tier] || !image) return;
  compositeFreed[tier] = true;
  releaseCanvasTexture(image);
}

/** Device px per CSS px of the Fall draw (Pixi renderer resolution, or the 2D canvas DPR). */
function deviceScaleOf(playfield) {
  if (playfield && playfield.resolution > 0) return playfield.resolution;
  return MergeAssets && typeof MergeAssets.canvasDeviceScale === "function" ? MergeAssets.canvasDeviceScale() : 1;
}

/**
 * Screen-axis scale (a, b) seen from a sprite turned by `angle`: the diagonal of
 * R(−angle)·diag(a, b)·R(angle). A sprite scales along its own axes only, so this is the
 * closest it gets to a squash that stays upright while it spins.
 */
function localScale(a, b, angle) {
  if (a === b || !angle) return { sx: a, sy: b };
  var c = Math.cos(angle), s = Math.sin(angle);
  var c2 = c * c, s2 = s * s;
  return { sx: a * c2 + b * s2, sy: a * s2 + b * c2 };
}

export function FallRenderer(runConfig, scale, mockupScale) {
    this.runConfig = runConfig || {};
    this.scale = scale || null;
    this.mockupScale = mockupScale || null;
    /** Juice API (`game.juice`), set by the game host; optional. */
    this.juice = null;
    // Render-side memory (never gameplay state): bodies already seen, hazard marker timers,
    // the last flask HP (hit flash), the last countdown digit and callout (one-shot FX).
    this._seen = new Map();
    this._seenGen = 0;
    this._hazardSince = { left: null, right: null };
    this._lastFlaskHp = null;
    this._flaskHitAt = null;
    this._countdownDigit = null;
    this._calloutAge = null;
    this._calloutText = null;
    this._shieldPopFor = null;
    // Caught creatures still being swallowed (ghosts of bodies that are gone), and the body ids
    // already handed to them, so the live body is not drawn as well.
    this._swallows = [];
    this._swallowIds = new Set();
    this._swallowCount = 0;
  }

  FallRenderer.prototype._getFlaskDims = function (runHeight) {
    var scale = this.scale;
    var flaskW = scale && scale.runwayFlaskDisplayWidth ? scale.runwayFlaskDisplayWidth : 120;
    var flaskH = scale && scale.runwayFlaskDisplayHeight ? scale.runwayFlaskDisplayHeight : 120;
    var topFromBottom = C.FLASK_TOP_FROM_BOTTOM_RATIO != null
      ? C.FLASK_TOP_FROM_BOTTOM_RATIO : 0.24;
    var desiredTopY = runHeight * (1 - topFromBottom);
    var bottomGap = runHeight * 0.018;
    var flaskTopY = Math.min(desiredTopY, runHeight - flaskH - bottomGap);
    if (flaskTopY < 0) flaskTopY = 0;
    return { flaskW: flaskW, flaskH: flaskH, flaskTopY: flaskTopY };
  };

  /** The juice API when the host attached one (`opts.juice` wins over `this.juice`). */
  FallRenderer.prototype._getJuice = function (opts) {
    var j = (opts && opts.juice) || this.juice;
    return j && j.engine ? j : null;
  };

  FallRenderer.prototype._getAppearMs = function () {
    var appearMs = this.runConfig.FALL_SPAWN_APPEAR_MS != null
      ? Number(this.runConfig.FALL_SPAWN_APPEAR_MS)
      : (C.RUN_CONFIG && C.RUN_CONFIG.FALL_SPAWN_APPEAR_MS != null
        ? Number(C.RUN_CONFIG.FALL_SPAWN_APPEAR_MS)
        : 260);
    return isFinite(appearMs) && appearMs > 0 ? appearMs : 260;
  };

  /**
   * Speed lines and stretch follow the motion trail switch (config + reduced motion) and wait
   * until the spawn pop is mostly done.
   */
  FallRenderer.prototype._shouldDrawMotionTrail = function (entry, currentTime, runHeight) {
    if (!entry || entry.suction) return false;
    if (!isFallMotionTrailEnabled(this.runConfig, { forceReducedMotion: prefersReducedMotion() })) {
      return false;
    }
    var appearMs = this._getAppearMs();
    if (entry.spawnTime != null && currentTime != null) {
      if ((currentTime - entry.spawnTime) < appearMs * 0.55) return false;
    }
    var body = entry.body;
    var vx = body && body.velocity && isFinite(body.velocity.x) ? body.velocity.x : 0;
    var vy = body && body.velocity && isFinite(body.velocity.y) ? body.velocity.y : 0;
    return fallMotionTrailIntensity(vx, vy, this.runConfig, runHeight) > 0;
  };

  /** Mark a body seen this frame; returns its render memo and whether it is new. */
  FallRenderer.prototype._noteBody = function (id) {
    var rec = this._seen.get(id);
    var first = !rec;
    if (!rec) {
      rec = { gen: 0, gulped: false };
      this._seen.set(id, rec);
    }
    rec.gen = this._seenGen;
    return { rec: rec, first: first };
  };

  FallRenderer.prototype._pruneSeen = function () {
    var gen = this._seenGen;
    var seen = this._seen;
    seen.forEach(function (rec, id) {
      if (rec.gen !== gen) seen.delete(id);
    });
  };

  /**
   * A fresh spawn: an inked cartoon poof it pops out of (smoke puffs blooming outward) with a
   * few sparkles in its colours; boulders get dust and no sparkles. Juice back layer, so the
   * object itself draws over its poof.
   */
  FallRenderer.prototype._spawnPoof = function (juice, type, obj, x, y, pixelW, pixelH, overObjects) {
    var E = juice.engine;
    var r = Math.max(pixelW, pixelH) * 0.5;
    if (!(r > 1)) return;
    var hazard = type === "boulder";
    var tone = hazard ? "dust" : "smoke";
    // A ring of puffs blooming outward (no puff in the middle: the object pops out there).
    // Canvas 2D draws juice over the objects, so there the ring starts further out.
    var start = overObjects ? 0.95 : 0.7;
    var n = hazard ? 5 : 6;
    var a0 = Math.random() * TAU;
    for (var i = 0; i < n; i++) {
      var a = a0 + (i / n) * TAU;
      var ca = Math.cos(a), sa = Math.sin(a);
      E.puff(x + ca * r * start, y + sa * r * start * 0.75, {
        tone: tone,
        size: r * (0.42 + Math.random() * 0.14),
        vx: ca * r * 1.9,
        vy: sa * r * 1.2 - 8,
        life: 0.3 + Math.random() * 0.1,
        layer: "back",
      });
    }
    if (hazard) return;
    var colors;
    if (type === "merge_pickup") {
      var base = tierColor(obj && obj.tier != null ? obj.tier : 0);
      colors = [base, "#ffffff", "#fff3b8"];
    } else if (type === "gem") {
      colors = ["#ffffff", "#ff9cf5", "#ff6fd8"];
    } else {
      colors = ["#fffbe6", "#ffd45c", "#ffc84a"];
    }
    E.sparkles(x, y, {
      count: 6, colors: colors, shape: "sparkle-ink", speed: [110, 200], size: [3.2, 5.4],
      life: [0.3, 0.5], radius: r * 0.5, gravity: 140, up: 30, drag: 3,
    });
  };

  /** Cartoon speed lines behind a fast mover (sprites under the object, or 2D images). */
  FallRenderer.prototype._drawSpeedLines = function (ctx, playfield, origin, id, px, py, size, vx, vy, intensity, animTime, alpha) {
    var lines = layoutFallSpeedLines(px, py, size, {
      velocity: { x: vx, y: vy },
      intensity: intensity,
      seed: typeof id === "number" ? id : 0,
      timeMs: animTime,
    });
    if (!lines.length) return;
    var img = getFallFxArt("speedLine");
    if (!img) return;
    var a = alpha == null ? 1 : alpha;
    for (var i = 0; i < lines.length; i++) {
      var L = lines[i];
      if (playfield) {
        playfield.sprite({
          key: "fall-sl-" + id + "-" + i,
          image: img,
          x: origin.x + L.x,
          y: origin.y + L.y,
          width: L.length,
          height: L.width,
          rotation: L.rotation,
          alpha: a * L.alpha,
          zIndex: 14,
        });
        continue;
      }
      if (!ctx || typeof ctx.drawImage !== "function") continue;
      ctx.save();
      ctx.globalAlpha = (ctx.globalAlpha != null ? ctx.globalAlpha : 1) * a * L.alpha;
      ctx.translate(L.x, L.y);
      ctx.rotate(L.rotation);
      ctx.drawImage(img, -L.length / 2, -L.width / 2, L.length, L.width);
      ctx.restore();
    }
  };

  /**
   * drawObjects — draw all physics bodies at their current position.
   * For sucked objects, apply a slurp (stretch, shrink, fade) over the suction period.
   */
  FallRenderer.prototype.drawObjects = function (ctx, bodyEntries, runWidth, runHeight, currentTime, opts) {
    opts = opts || {};
    var animEnabled = C.FALL_SUCTION_ANIMATION_ENABLED != null
      ? C.FALL_SUCTION_ANIMATION_ENABLED : true;
    var suctionMs = C.FALL_SUCTION_ANIMATION_MS != null
      ? C.FALL_SUCTION_ANIMATION_MS : 50;
    var edgeHazard = { left: 0, right: 0 };
    var animTime = opts.animTime != null ? opts.animTime : currentTime;
    var reduced = prefersReducedMotion();
    var juice = this._getJuice(opts);
    var origin = this._worldOrigin(opts);
    var playfield = opts.playfield && typeof opts.playfield.sprite === "function" ? opts.playfield : null;
    var popMs = this._getAppearMs() * FALL_POP_DURATION_MUL;
    var slot = 0;
    this._seenGen++;

    for (var i = 0; i < bodyEntries.length; i++) {
      var entry = bodyEntries[i];
      var obj = entry.obj;
      var body = entry.body;
      if (!obj || !body) continue;
      if (obj.collected || obj.caught) continue;
      var type = obj.getType ? obj.getType() : "";

      // When animation is disabled, suctioned objects vanish immediately
      if (entry.suction && !animEnabled) continue;

      var id = body.id != null ? body.id : (type + "-" + i);
      // A caught creature is drawn by drawSwallowed from here on.
      if (entry.suction && this._swallowIds.has(id)) continue;
      var px = body.position.x;
      var py = body.position.y;
      var drawWidthFrac = obj.width;
      var pixelW = fallObjectPixelWidth(drawWidthFrac, runWidth, this.runConfig);
      var aspect = obj.getAspectRatio ? obj.getAspectRatio() : 1;
      var pixelH = pixelW / (aspect > 0 ? aspect : 1);
      if (type === "bonus") {
        this._drawSpawnTelegraph(ctx, type, px, py, pixelW, runHeight, animTime, opts, id);
      }
      if (type === "boulder" && py < runHeight * 0.5) {
        var edgeBand = runWidth * 0.14;
        var yStrength = Math.max(0, 1 - (py / (runHeight * 0.5)));
        if (px < edgeBand) edgeHazard.left = Math.max(edgeHazard.left, yStrength);
        if (px > runWidth - edgeBand) edgeHazard.right = Math.max(edgeHazard.right, yStrength);
      }

      var noted = this._noteBody(id);
      var spawnAge = entry.spawnTime != null && currentTime != null ? currentTime - entry.spawnTime : null;
      if (noted.first && juice && type !== "boulder_debris" && spawnAge != null && spawnAge < 220) {
        this._spawnPoof(juice, type, obj, origin.x + px, origin.y + py, pixelW, pixelH, !playfield);
      }

      var vx = body.velocity && isFinite(body.velocity.x) ? body.velocity.x : 0;
      var vy = body.velocity && isFinite(body.velocity.y) ? body.velocity.y : 0;
      var angle = typeof body.angle === "number" ? body.angle : 0;
      // look: overall scale + alpha, plus a screen-axis squash (pop) and a stretch along the velocity.
      var scale = 1;
      var alpha = 1;
      var popSx = 1;
      var popSy = 1;
      var stretch = null;
      var trailOn = false;
      var intensity = 0;

      if (entry.suction) {
        // Slurp: stretched toward the mouth while it shrinks and fades.
        var elapsed = currentTime - entry.suction.startTime;
        var t = Math.min(1, Math.max(0, elapsed / suctionMs));
        scale = 1 - t * 0.7;
        alpha = 1 - t;
        if (!reduced) {
          popSy = 1 + t * 0.35;
          popSx = 1 / popSy;
        }
        if (alpha <= 0) continue;
        if (juice && !noted.rec.gulped && type !== "boulder" && type !== "boulder_debris") {
          noted.rec.gulped = true;
          juice.engine.pulse(GULP_SPRING, 1, { freq: 26, damp: 10 });
        }
      } else {
        if (spawnAge != null && popMs > 0) {
          var pop = fallAppearPop(spawnAge / popMs);
          if (reduced) {
            alpha = pop.alpha;
          } else {
            scale = pop.scale;
            alpha = pop.alpha;
            popSx = pop.sx;
            popSy = pop.sy;
          }
        }
        trailOn = this._shouldDrawMotionTrail(entry, currentTime, runHeight);
        if (trailOn) {
          intensity = fallMotionTrailIntensity(vx, vy, this.runConfig, runHeight);
          var maxStretch = STRETCH_BY_TYPE[type];
          if (maxStretch) stretch = fallVelocityStretch(intensity, vx, vy, maxStretch);
        }
      }

      var z = 15 + (slot % 80) * 0.01;
      slot++;
      if (trailOn && intensity > 0 && SPEED_LINE_TYPES[type]) {
        this._drawSpeedLines(
          ctx, playfield, origin, id, px, py, Math.max(pixelW, pixelH) * scale,
          vx, vy, intensity, animTime, alpha
        );
      }
      this._drawFallObject(ctx, playfield, origin, {
        obj: obj,
        body: body,
        type: type,
        id: id,
        px: px,
        py: py,
        pixelW: pixelW,
        pixelH: pixelH,
        angle: angle,
        scale: scale,
        alpha: alpha,
        popSx: popSx,
        popSy: popSy,
        stretch: stretch,
        animTime: animTime,
        z: z,
        rec: noted.rec,
      });

      // Legacy spawn burst only when no juice engine is attached (the poof replaces it).
      if (!juice
        && areRuntimeParticlesEnabled()
        && entry.spawnParticles
        && entry.spawnParticlesUntil != null
        && currentTime < entry.spawnParticlesUntil) {
        var spawnParticleMs = this.runConfig.FALL_SPAWN_PARTICLE_MS != null
          ? Number(this.runConfig.FALL_SPAWN_PARTICLE_MS)
          : (C.RUN_CONFIG && C.RUN_CONFIG.FALL_SPAWN_PARTICLE_MS != null
            ? Number(C.RUN_CONFIG.FALL_SPAWN_PARTICLE_MS)
            : 350);
        drawMergeParticles(
          ctx,
          entry.spawnParticles,
          entry.spawnParticlesStart || entry.spawnTime || 0,
          spawnParticleMs,
          currentTime,
          {
            playfield: opts.playfield,
            origin: origin,
            keyPrefix: "spawn-" + id,
          }
        );
      }
    }
    this._pruneSeen();
    this._drawEdgeHazardIndicators(ctx, runWidth, runHeight, edgeHazard, currentTime, opts);
  };

  /**
   * One falling object. `d.stretch` is a stretch along the velocity (screen space); the pop
   * squash (popSx, popSy) is along the screen axes. Creatures with baked layers draw as disc
   * (upright) + creature (turned with the body; blink now and then) + shine (upright); other
   * objects draw as one picture turned with the body.
   */
  FallRenderer.prototype._drawFallObject = function (ctx, playfield, origin, d) {
    var obj = d.obj;
    var layers = null;
    if (d.type === "merge_pickup") {
      // Report the resting on-screen size first, so the tier is baked fine enough (and no finer).
      if (MergeAssets && typeof MergeAssets.noteLayerSize === "function") {
        MergeAssets.noteLayerSize(obj.tier, Math.max(d.pixelW, d.pixelH) * deviceScaleOf(playfield), "fall");
      }
      layers = getFallCreatureLayers(obj.tier);
    }
    var st = d.stretch && d.stretch.e > 0 ? d.stretch : null;
    var screen = stretchAxes(st, 0);
    // Screen-axis scale of the whole object (pop squash × stretch, diagonal approximation).
    var ax = d.popSx * screen.sx;
    var ay = d.popSy * screen.sy;
    var s = d.scale;
    // The shared glint's frame vs the tier frame (a few px apart on some tiers).
    var shineKx = layers && layers.shineFrame ? layers.shineFrame.w / layers.frame.w : 1;
    var shineKy = layers && layers.shineFrame ? layers.shineFrame.h / layers.frame.h : 1;
    if (playfield) {
      var img = typeof obj.getDrawImage === "function" ? obj.getDrawImage() : null;
      if (layers && img) {
        var w = d.pixelW * s;
        var h = d.pixelH * s;
        var bodyImg = fallBlinkClosed(typeof d.id === "number" ? d.id : 0, d.animTime) ? layers.blink : layers.body;
        var turned = localScale(ax, ay, d.angle);
        var x = origin.x + d.px;
        var y = origin.y + d.py;
        var keys = this._layerKeys(d);
        freeCompositeTexture(obj.tier, img);
        playfield.sprite({ key: keys.disc, image: layers.disc, x: x, y: y, width: w * ax, height: h * ay, rotation: 0, alpha: d.alpha, zIndex: d.z });
        playfield.sprite({ key: keys.body, image: bodyImg, x: x, y: y, width: w * turned.sx, height: h * turned.sy, rotation: d.angle, alpha: d.alpha, zIndex: d.z + 0.003 });
        if (layers.shine) {
          playfield.sprite({ key: keys.shine, image: layers.shine, x: x, y: y, width: w * ax * shineKx, height: h * ay * shineKy, rotation: 0, alpha: d.alpha, zIndex: d.z + 0.006 });
        }
        return;
      }
      var local = localScale(ax, ay, d.angle);
      if (this._syncObjectSprite(
        playfield, origin, obj, d.body, d.px, d.py, d.pixelW, d.pixelH, d.alpha, s, d.angle, d.z, local.sx, local.sy
      )) {
        return;
      }
    }
    if (!ctx) return;
    ctx.save();
    if (d.alpha < 1) ctx.globalAlpha = d.alpha;
    ctx.translate(d.px, d.py);
    // Canvas 2D can stretch exactly along the velocity: R(φ)·diag(1+e, 1/(1+e))·R(−φ).
    if (st && typeof ctx.rotate === "function") {
      var phi = Math.atan2(st.dy, st.dx);
      ctx.rotate(phi);
      ctx.scale(1 + st.e, 1 / (1 + st.e));
      ctx.rotate(-phi);
    }
    if (d.popSx !== 1 || d.popSy !== 1) ctx.scale(d.popSx, d.popSy);
    if (s !== 1) ctx.scale(s, s);
    var pw = d.pixelW;
    var ph = d.pixelH;
    if (layers && typeof ctx.drawImage === "function") {
      ctx.drawImage(layers.disc, -pw / 2, -ph / 2, pw, ph);
      if (Math.abs(d.angle) > 1e-4) ctx.rotate(d.angle);
      var blink = fallBlinkClosed(typeof d.id === "number" ? d.id : 0, d.animTime);
      ctx.drawImage(blink ? layers.blink : layers.body, -pw / 2, -ph / 2, pw, ph);
      if (Math.abs(d.angle) > 1e-4) ctx.rotate(-d.angle);
      if (layers.shine) {
        var sw = pw * shineKx, sh = ph * shineKy;
        ctx.drawImage(layers.shine, -sw / 2, -sh / 2, sw, sh);
      }
    } else {
      if (Math.abs(d.angle) > 1e-4) ctx.rotate(d.angle);
      obj.draw(ctx, -pw / 2, -ph / 2, pw, ph);
    }
    ctx.restore();
  };

  /** Sprite keys of a creature's layers, built once per body (kept in its render memo). */
  FallRenderer.prototype._layerKeys = function (d) {
    var rec = d.rec;
    if (rec && rec.keys) return rec.keys;
    var base = "fall-" + d.id;
    var keys = { disc: base + "-disc", body: base + "-body", shine: base + "-shine" };
    if (rec) rec.keys = keys;
    return keys;
  };

  /**
   * Shield pickup telegraph: an inked dashed gold ring turning round the falling shield near
   * the top of the column, pulsing.
   */
  FallRenderer.prototype._drawSpawnTelegraph = function (ctx, type, px, py, pixelW, runHeight, animTime, opts, id) {
    if (py > runHeight * 0.22) return;
    if (type !== "bonus") return;
    var t = animTime || 0;
    var pulse = 0.5 + 0.5 * Math.sin(t * 0.012);
    var fade = Math.max(0, 1 - (py / (runHeight * 0.22)));
    var alpha = (0.6 + 0.4 * pulse) * fade;
    if (!(alpha > 0.01)) return;
    var radius = Math.max(14, pixelW * (0.64 + pulse * 0.1));
    var rot = t * 0.0016;
    var img = getFallFxArt("telegraphRing");
    var playfield = opts && opts.playfield;
    var origin = this._worldOrigin(opts);
    if (img) {
      var size = (radius * TELEGRAPH_RING_FRAME.size) / TELEGRAPH_RING_FRAME.radius;
      if (playfield && typeof playfield.underFx === "function") {
        playfield.underFx({
          key: "telegraph-" + id,
          kind: "texture",
          image: img,
          x: origin.x + px,
          y: origin.y + py,
          width: size,
          height: size,
          rotation: rot,
          alpha: alpha,
          zIndex: 4,
        });
        return;
      }
      if (!ctx || typeof ctx.drawImage !== "function") return;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(px, py);
      ctx.rotate(rot);
      ctx.drawImage(img, -size / 2, -size / 2, size, size);
      ctx.restore();
      return;
    }
    if (playfield && typeof playfield.underFx === "function") {
      playfield.underFx({
        key: "telegraph-" + id,
        kind: "ring",
        x: origin.x + px,
        y: origin.y + py,
        radius: radius,
        stroke: 3,
        rgb: [255, 200, 74],
        alpha: alpha,
        zIndex: 4,
      });
      return;
    }
    if (!ctx || typeof ctx.arc !== "function") return;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = "#ffc84a";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(px, py, radius, 0, TAU);
    ctx.stroke();
    ctx.restore();
  };

  /**
   * Edge hazard markers: an inked red/yellow warning sign with a down chevron at the column
   * edge while a boulder comes down near it, hopping (with a landing squash) and popping in.
   */
  FallRenderer.prototype._drawEdgeHazardIndicators = function (ctx, runWidth, runHeight, edgeHazard, currentTime, opts) {
    var since = this._hazardSince;
    // Most frames have no hazard: skip the setup (and the marker art lookup) entirely.
    if (!(edgeHazard.left > 0) && !(edgeHazard.right > 0)) {
      since.left = null;
      since.right = null;
      return;
    }
    opts = opts || {};
    var t = opts.animTime != null ? opts.animTime : (currentTime || 0);
    var reduced = prefersReducedMotion();
    var playfield = opts.playfield;
    var origin = this._worldOrigin(opts);
    var img = getFallFxArt("warnMarker");
    var dims = this._getFlaskDims(runHeight);
    var mh = clamp(dims.flaskW * 0.46, 36, 70);
    var mw = (mh * WARN_MARKER_FRAME.w) / WARN_MARKER_FRAME.h;
    // Low in the column, pointing down at the catch level: the marker shows while the boulder
    // is still in the top half, so the boulder never covers it and the player, watching the
    // flask, sees it.
    var tipY = Math.min(runHeight * 0.66, dims.flaskTopY - mh * 0.25);
    for (var i = 0; i < HAZARD_SIDES.length; i++) {
      var side = HAZARD_SIDES[i];
      var strength = edgeHazard[side];
      if (!(strength > 0)) {
        since[side] = null;
        continue;
      }
      if (since[side] == null) since[side] = t;
      var alpha = 0.5 + 0.5 * strength;
      if (!img) {
        this._drawEdgeHazardTriangle(ctx, side, runWidth, runHeight, alpha * 0.6, t, playfield, origin);
        continue;
      }
      var hop = reduced ? { lift: 0, sx: 1, sy: 1, scale: 1 } : warnMarkerHop(t + (side === "right" ? 140 : 0), t - since[side]);
      var w = mw * hop.sx * hop.scale;
      var h = mh * hop.sy * hop.scale;
      var inset = Math.max(mw * 0.5 + 6, runWidth * 0.06);
      var x = side === "left" ? inset : runWidth - inset;
      var bottom = tipY - hop.lift * mh * 0.2;
      var y = bottom - h / 2;
      if (playfield && typeof playfield.effect === "function") {
        playfield.effect({
          key: "hazard-" + (side === "left" ? "l" : "r"),
          kind: "texture",
          image: img,
          x: origin.x + x,
          y: origin.y + y,
          width: w,
          height: h,
          alpha: alpha,
          zIndex: 25,
        });
        continue;
      }
      if (!ctx || typeof ctx.drawImage !== "function") continue;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.drawImage(img, x - w / 2, y - h / 2, w, h);
      ctx.restore();
    }
  };

  /** Fallback edge marker without baked art (tests / no 2D canvas): a plain inked triangle. */
  FallRenderer.prototype._drawEdgeHazardTriangle = function (ctx, side, runWidth, runHeight, alpha, t, playfield, origin) {
    var top = runHeight * 0.2;
    var mid = runHeight * 0.35;
    var bottom = runHeight * 0.5;
    var pts = side === "left"
      ? [0, mid, 18, top, 18, bottom]
      : [runWidth, mid, runWidth - 18, top, runWidth - 18, bottom];
    if (playfield && typeof playfield.effect === "function") {
      var gp = [];
      for (var i = 0; i < pts.length; i += 2) gp.push(pts[i] + origin.x, pts[i + 1] + origin.y);
      playfield.effect({
        key: "hazard-" + (side === "left" ? "l" : "r"),
        kind: "poly",
        points: gp,
        rgb: [239, 68, 68],
        alpha: alpha,
        zIndex: 25,
      });
      return;
    }
    if (!ctx || typeof ctx.fill !== "function") return;
    ctx.save();
    ctx.fillStyle = "rgba(239, 68, 68," + alpha.toFixed(3) + ")";
    ctx.beginPath();
    ctx.moveTo(pts[0], pts[1]);
    ctx.lineTo(pts[2], pts[3]);
    ctx.lineTo(pts[4], pts[5]);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  };

  FallRenderer.prototype._worldOrigin = function (opts) {
    opts = opts || {};
    return {
      x: opts.worldOrigin && opts.worldOrigin.x != null ? opts.worldOrigin.x : 0,
      y: opts.worldOrigin && opts.worldOrigin.y != null ? opts.worldOrigin.y : 0,
    };
  };

  /**
   * One GPU sprite for a falling object. `sx`/`sy` (optional) squash it along its own axes on
   * top of the uniform `scale`.
   */
  FallRenderer.prototype._syncObjectSprite = function (playfield, origin, obj, body, px, py, pixelW, pixelH, alpha, scale, angle, zIndex, sx, sy) {
    if (!playfield || typeof playfield.sprite !== "function" || !obj) return false;
    var img = typeof obj.getDrawImage === "function" ? obj.getDrawImage() : null;
    if (!img) return false;
    var s = scale != null && isFinite(scale) ? scale : 1;
    var kx = sx != null && isFinite(sx) ? sx : 1;
    var ky = sy != null && isFinite(sy) ? sy : 1;
    var size = fallObjectSpriteSize(obj, pixelW, pixelH);
    playfield.sprite({
      key: "fall-" + (body && body.id != null ? body.id : obj.getType && obj.getType()),
      image: img,
      x: origin.x + px,
      y: origin.y + py,
      width: size.width * s * kx,
      height: size.height * s * ky,
      rotation: angle || 0,
      alpha: alpha == null ? 1 : alpha,
      zIndex: zIndex != null ? zIndex : 15,
    });
    return true;
  };

  /**
   * Sea buoys at the flask's horizontal stop points (left/right travel limits).
   */
  FallRenderer.prototype.drawMovementRange = function (ctx, opts) {
    if (!opts || !opts.showMovementRange) return;
    var dims = this._getFlaskDims(opts.runHeight);
    var playerX = opts.playerX;
    if (playerX == null && opts.player) playerX = opts.player.x;
    if (playerX == null) playerX = opts.runWidth / 2;
    var origin = this._worldOrigin(opts);
    var fallPhysics = opts.fallPhysics;
    if (opts.playfield) {
      var specs = collectFallBuoySpriteSpecs({
        runWidth: opts.runWidth,
        runHeight: opts.runHeight,
        playerX: playerX,
        scale: opts.scale || this.scale,
        scaleUi: this._getMockupScaleSide(),
        flaskDims: dims,
        currentTime: opts.animTime != null ? opts.animTime : opts.currentTime,
        stageIntroAlpha: opts.catcherFlaskIntroAlpha,
        originX: origin.x,
        originY: origin.y,
      });
      if (specs.length) {
        for (var i = 0; i < specs.length; i++) opts.playfield.sprite(specs[i]);
        return;
      }
    }
    drawFallMovementRange(ctx, {
      runWidth: opts.runWidth,
      runHeight: opts.runHeight,
      playerX: playerX,
      scale: opts.scale || this.scale,
      scaleUi: this._getMockupScaleSide(),
      flaskDims: dims,
      currentTime: opts.animTime != null ? opts.animTime : opts.currentTime,
      stageIntroAlpha: opts.catcherFlaskIntroAlpha,
      runConfig: this.runConfig,
      flaskGeometry: fallPhysics && fallPhysics.flaskGeometry ? fallPhysics.flaskGeometry : null,
    });
  };

  /**
   * drawSwallowed — caught creatures being sucked into the flask (run/fall-swallow.js). A
   * creature whose catch has started is taken over here: it shoots into the mouth, drops into
   * the potion and dissolves, behind the flask glass (Pixi z under the skin; on Canvas 2D drawn
   * before the flask). The flask gulps as it passes the mouth. Presentation only.
   */
  FallRenderer.prototype.drawSwallowed = function (ctx, bodyEntries, player, runWidth, runHeight, introAlpha, opts) {
    var animEnabled = C.FALL_SUCTION_ANIMATION_ENABLED != null ? C.FALL_SUCTION_ANIMATION_ENABLED : true;
    if (!animEnabled) return;
    opts = opts || {};
    var t = opts.animTime != null ? opts.animTime : opts.currentTime;
    if (t == null || !isFinite(t)) return;
    var i;
    for (i = 0; i < bodyEntries.length; i++) {
      var entry = bodyEntries[i];
      var obj = entry && entry.obj;
      var body = entry && entry.body;
      if (!entry || !entry.suction || !obj || !body) continue;
      if ((obj.getType ? obj.getType() : "") !== "merge_pickup") continue;
      var id = body.id != null ? body.id : null;
      if (id == null || this._swallowIds.has(id)) continue;
      var pixelW = fallObjectPixelWidth(obj.width, runWidth, this.runConfig);
      var aspect = obj.getAspectRatio ? obj.getAspectRatio() : 1;
      var pixelH = pixelW / (aspect > 0 ? aspect : 1);
      var n = this._swallowCount++;
      this._swallowIds.add(id);
      this._swallows.push({
        id: id,
        obj: obj,
        slot: n,
        t0: t,
        pixelW: pixelW,
        pixelH: pixelH,
        start: {
          x: body.position.x,
          y: body.position.y,
          size: Math.max(pixelW, pixelH),
          angle: typeof body.angle === "number" ? body.angle : 0,
        },
        gulped: false,
        rec: { keys: { disc: "swallow-" + n + "-disc", body: "swallow-" + n + "-body", shine: "swallow-" + n + "-shine" } },
      });
    }
    if (!this._swallows.length) return;
    var dims = this._getFlaskDims(runHeight);
    var frame = swallowFrame(player.x, dims);
    var mouth = { x: player.x, y: frame.y0 + SWALLOW_FRAME.mouthY * frame.s };
    var size = SWALLOW_FRAME.size * frame.s;
    var neckSize = SWALLOW_FRAME.neck * frame.s;
    var reduced = prefersReducedMotion();
    var juice = this._getJuice(opts);
    var squash = juice && !reduced
      ? catcherFlaskSquash(juice.engine.pulseValue("flask"), juice.engine.pulseValue(GULP_SPRING))
      : { sx: 1, sy: 1 };
    var base = dims.flaskTopY + dims.flaskH;
    var fade = introAlpha != null && isFinite(introAlpha) ? Math.max(0, Math.min(1, introAlpha)) : 1;
    var origin = this._worldOrigin(opts);
    var playfield = opts.playfield && typeof opts.playfield.sprite === "function" ? opts.playfield : null;
    var keep = [];
    for (i = 0; i < this._swallows.length; i++) {
      var g = this._swallows[i];
      var pose = swallowPose(t - g.t0, g.start, mouth, swallowRest(frame, g.slot), size, reduced, neckSize);
      if (!pose) {
        this._swallowIds.delete(g.id);
        continue;
      }
      keep.push(g);
      var x = pose.x;
      var y = pose.y;
      var k = 1;
      if (pose.inside) {
        // Inside, it squashes with the flask (about the flask's base).
        x = player.x + (x - player.x) * squash.sx;
        y = base - (base - y) * squash.sy;
        k = Math.sqrt(squash.sx * squash.sy);
        if (!g.gulped && juice) juice.engine.pulse(GULP_SPRING, 1, { freq: 26, damp: 10 });
        g.gulped = true;
      }
      this._drawFallObject(ctx, playfield, origin, {
        obj: g.obj,
        body: null,
        type: "merge_pickup",
        id: g.id,
        px: x,
        py: y,
        pixelW: g.pixelW,
        pixelH: g.pixelH,
        angle: pose.angle,
        scale: (pose.size * k) / g.start.size,
        alpha: pose.alpha * fade,
        popSx: 1,
        popSy: 1,
        stretch: null,
        animTime: t,
        z: 9.5 + (g.slot % 40) * 0.01,
        rec: g.rec,
      });
    }
    this._swallows = keep;
  };

  /**
   * drawFlask — player-controlled runway flask sprite. Squashes about its base on the juice
   * springs (boulder hit, gulp) and flashes white for a beat when it loses HP.
   */
  FallRenderer.prototype.drawFlask = function (ctx, player, runWidth, runHeight, currentTime, introAlpha, opts) {
    if (!player || player.hp <= 0) {
      this._lastFlaskHp = player ? player.hp : null;
      return;
    }
    var dims = this._getFlaskDims(runHeight);
    var flaskW = dims.flaskW;
    var flaskH = dims.flaskH;
    var flaskTopY = dims.flaskTopY;

    var flaskX = player.x - flaskW / 2;
    var flaskY = flaskTopY;
    var alpha = introAlpha != null && isFinite(introAlpha) ? Math.max(0, Math.min(1, introAlpha)) : 1;
    opts = opts || {};
    var t = opts.animTime != null ? opts.animTime : currentTime;
    if (this._lastFlaskHp != null && player.hp < this._lastFlaskHp && t != null) this._flaskHitAt = t;
    this._lastFlaskHp = player.hp;
    var flash = 0;
    if (this._flaskHitAt != null && t != null) {
      var age = t - this._flaskHitAt;
      if (age >= 0 && age < FLASK_HIT_FLASH_MS) flash = 0.9 * (1 - age / FLASK_HIT_FLASH_MS);
    }
    var juice = this._getJuice(opts);
    var squash = { sx: 1, sy: 1 };
    if (juice && !prefersReducedMotion()) {
      squash = catcherFlaskSquash(juice.engine.pulseValue("flask"), juice.engine.pulseValue(GULP_SPRING));
    }
    var playfield = opts.playfield;
    if (playfield && FlaskRenderer.collectFlaskVisualSprites) {
      var origin = this._worldOrigin(opts);
      var collected = FlaskRenderer.collectFlaskVisualSprites(
        origin.x + flaskX,
        origin.y + flaskY,
        flaskW,
        flaskH,
        player.hp,
        "catcher"
      );
      var specs = collected && collected.specs ? collected.specs : collected;
      if (specs && specs.length) {
        var cx = origin.x + player.x;
        var base = origin.y + flaskY + flaskH;
        var flashImg = flash > 0 ? getFallFxArt("flaskFlash") : null;
        if (flashImg && specs[0]) {
          specs.push({
            key: "catcher-flash",
            image: flashImg,
            x: specs[0].x,
            y: specs[0].y,
            width: specs[0].width,
            height: specs[0].height,
            alpha: flash,
            anchorX: 0.5,
            anchorY: 0.5,
            zIndex: 12.5,
          });
        }
        for (var i = 0; i < specs.length; i++) {
          var sp = specs[i];
          if (squash.sx !== 1 || squash.sy !== 1) {
            sp.x = cx + (sp.x - cx) * squash.sx;
            sp.y = base - (base - sp.y) * squash.sy;
            sp.width *= squash.sx;
            sp.height *= squash.sy;
          }
          sp.alpha = (sp.alpha == null ? 1 : sp.alpha) * alpha;
          playfield.sprite(sp);
        }
        return;
      }
    }

    ctx.save();
    if (alpha < 1) ctx.globalAlpha = alpha;
    if ((squash.sx !== 1 || squash.sy !== 1) && typeof ctx.scale === "function") {
      var bx = player.x;
      var by = flaskY + flaskH;
      ctx.translate(bx, by);
      ctx.scale(squash.sx, squash.sy);
      ctx.translate(-bx, -by);
    }
    if (FlaskRenderer && FlaskRenderer.drawFlaskAtRect) {
      FlaskRenderer.drawFlaskAtRect(ctx, flaskX, flaskY, flaskW, flaskH, player.hp);
    }
    var flash2d = flash > 0 ? getFallFxArt("flaskFlash") : null;
    if (flash2d && typeof ctx.drawImage === "function") {
      // Same letterbox as the flask skin (both pictures share the 256×383 frame).
      var aspect = flash2d.naturalHeight > 0 && flash2d.naturalWidth > 0 ? flash2d.naturalHeight / flash2d.naturalWidth : 383 / 256;
      var dw = flaskW;
      var dh = flaskW * aspect;
      if (dh > flaskH) {
        dh = flaskH;
        dw = flaskH / aspect;
      }
      ctx.globalAlpha = alpha * flash;
      ctx.drawImage(flash2d, flaskX + (flaskW - dw) / 2, flaskY + (flaskH - dh) / 2, dw, dh);
    }
    ctx.restore();
  };

  /**
   * drawShieldGlow — the cartoon bubble shield round the flask: translucent cel bubble with an
   * inked gold rim and a lit arc, gold hexes twinkling round its edge, a living wobble (and a
   * jolt when the flask is hit). It pops in with overshoot on pickup; on a block it swells for
   * a beat and bursts into droplets. Timing comes from the player's shield tween.
   */
  FallRenderer.prototype.drawShieldGlow = function (ctx, player, runWidth, runHeight, currentTime, opts) {
    if (!player || player.hp <= 0) return;
    // Read the tween before getShieldVisual, which clears it once it has run out.
    var fx = player.shieldFx && player.shieldFx.kind ? { kind: player.shieldFx.kind, t0: player.shieldFx.t0, ref: player.shieldFx } : null;
    var visual = typeof player.getShieldVisual === "function"
      ? player.getShieldVisual(currentTime)
      : (player.hasShield ? { alpha: 1, scale: 1 } : null);
    if (!visual || !(visual.alpha > 0.01)) return;

    var cfg = this.runConfig || {};
    var reduced = prefersReducedMotion();
    var dims = this._getFlaskDims(runHeight);
    var flaskW = dims.flaskW;
    var flaskH = dims.flaskH;
    var flaskCx = player.x;
    var flaskCy = dims.flaskTopY + flaskH / 2;
    var shieldR = Math.sqrt((flaskW / 2) * (flaskW / 2) + (flaskH / 2) * (flaskH / 2));
    var ringR = shieldR * 1.05;
    var t = currentTime || 0;
    // Without a tween to read (reduced motion, or a host that only exposes getShieldVisual)
    // the bubble just follows the visual's alpha.
    var look = reduced || !fx
      ? { scale: 1, alpha: visual.alpha, wobble: 0, popped: false, phase: "on" }
      : shieldBubbleLook(fx, t, { appearMs: cfg.SHIELD_APPEAR_MS, disappearMs: cfg.SHIELD_DISAPPEAR_MS });
    var juice = this._getJuice(opts);
    var origin = this._worldOrigin(opts);
    if (look.popped && fx && this._shieldPopFor !== fx.ref) {
      this._shieldPopFor = fx.ref;
      if (juice) this._shieldPopFx(juice, origin.x + flaskCx, origin.y + flaskCy, ringR * look.scale);
    }
    var alpha = Math.max(0, Math.min(1, look.alpha));
    if (!(alpha > 0.01)) return;
    var hit = juice ? juice.engine.pulseValue("flask") : 0;
    var wob = reduced ? 0 : 0.024 * Math.sin(t * 0.0047) + look.wobble + clamp(hit, -1.5, 1.5) * 0.07;
    var sx = look.scale * (1 + wob);
    var sy = look.scale * (1 - wob);
    var img = getFallFxArt("shieldBubble");
    var hexImg = getFallFxArt("shieldHex");
    var playfield = opts && opts.playfield;
    var gpu = !!(playfield && typeof playfield.effect === "function");
    var dim = (2 * ringR * (SHIELD_BUBBLE_FRAME.size / 2)) / SHIELD_BUBBLE_FRAME.radius;
    var x = gpu ? origin.x + flaskCx : flaskCx;
    var y = gpu ? origin.y + flaskCy : flaskCy;

    if (!img) {
      this._drawShieldFallback(ctx, playfield, x, y, ringR, sx, sy, alpha);
      return;
    }
    // Hex twinkles riding round the rim, each on its own beat.
    var hexes = [];
    if (hexImg && !reduced) {
      for (var i = 0; i < 5; i++) {
        var tw = Math.sin(t * 0.0042 + i * 1.93);
        if (tw <= 0.05) continue;
        var a = t * 0.00035 + (i / 5) * TAU + 0.4;
        var rr = ringR * 0.78;
        var hs = ringR * 0.26 * tw * tw;
        hexes.push({
          x: x + Math.cos(a) * rr * sx,
          y: y + Math.sin(a) * rr * sy,
          size: hs,
          rot: a * 0.5,
          alpha: alpha * Math.min(1, tw * 1.6),
        });
      }
    }
    if (gpu) {
      playfield.effect({
        key: "shield-bubble",
        kind: "texture",
        image: img,
        x: x,
        y: y,
        width: dim * sx,
        height: dim * sy,
        alpha: alpha,
        zIndex: 29,
      });
      for (var h = 0; h < hexes.length; h++) {
        playfield.effect({
          key: "shield-hex-" + h,
          kind: "texture",
          image: hexImg,
          x: hexes[h].x,
          y: hexes[h].y,
          width: hexes[h].size,
          height: hexes[h].size,
          rotation: hexes[h].rot,
          alpha: hexes[h].alpha,
          zIndex: 30,
        });
      }
      return;
    }
    if (!ctx || typeof ctx.drawImage !== "function") return;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.drawImage(img, x - (dim * sx) / 2, y - (dim * sy) / 2, dim * sx, dim * sy);
    for (var k = 0; k < hexes.length; k++) {
      var hx = hexes[k];
      ctx.save();
      ctx.globalAlpha = hx.alpha;
      ctx.translate(hx.x, hx.y);
      ctx.rotate(hx.rot);
      ctx.drawImage(hexImg, -hx.size / 2, -hx.size / 2, hx.size, hx.size);
      ctx.restore();
    }
    ctx.restore();
  };

  /** The bubble bursts: droplets flung off its rim and a few gold glints (juice, canvas px). */
  FallRenderer.prototype._shieldPopFx = function (juice, cx, cy, R) {
    var E = juice.engine;
    var U = E.unit || 1;
    E.sparkles(cx, cy, {
      count: 14, shape: "bubble", radius: R * 0.96, onRing: true, speed: [70, 170],
      size: [3.5 * U, 6.5 * U], life: [0.35, 0.6], gravity: 320, up: 30, drag: 1.6,
    });
    E.sparkles(cx, cy, {
      count: 10, colors: ["#fff3b8", "#ffc84a", "#ffffff"], shape: "sparkle-ink", radius: R * 0.92,
      onRing: true, speed: [120, 240], size: [3, 5.5], life: [0.3, 0.55], gravity: 220, up: 20,
    });
  };

  /** Bubble without baked art (tests / no 2D canvas): inked gold ring over a faint fill. */
  FallRenderer.prototype._drawShieldFallback = function (ctx, playfield, x, y, ringR, sx, sy, alpha) {
    var R = ringR * (sx + sy) / 2;
    if (playfield && typeof playfield.effect === "function") {
      playfield.effect({ key: "shield-ring-a", kind: "ring", x: x, y: y, radius: R, stroke: 6, rgb: [23, 42, 58], alpha: 0.85 * alpha, zIndex: 30 });
      playfield.effect({ key: "shield-ring-b", kind: "ring", x: x, y: y, radius: R, stroke: 3, rgb: [255, 200, 74], alpha: alpha, zIndex: 31 });
      return;
    }
    if (!ctx || typeof ctx.arc !== "function") return;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.arc(x, y, R, 0, TAU);
    ctx.fillStyle = "rgba(255, 220, 120, 0.16)";
    ctx.fill();
    ctx.lineWidth = Math.max(3, R * 0.05);
    ctx.strokeStyle = INK;
    ctx.stroke();
    ctx.lineWidth = Math.max(1.5, R * 0.025);
    ctx.strokeStyle = "#ffc84a";
    ctx.stroke();
    ctx.restore();
  };

  /**
   * Live mockup `--scale-side`. The game writes it inline on <html> at layout, and the inline
   * declaration reads without a style recalc; getComputedStyle here ran every frame (buoys,
   * countdown) and was the top JS self-time of the Fall loop. The computed value (a stylesheet
   * default) is only a fallback, re-read at most twice a second.
   */
  FallRenderer.prototype._getMockupScaleSide = function () {
    var inline = readInlineScaleSide();
    if (inline != null) return inline;
    var now = Date.now();
    if (this._scaleSideReadAt == null || now - this._scaleSideReadAt > SCALE_SIDE_REREAD_MS) {
      this._scaleSideReadAt = now;
      this._scaleSideComputed = readMockupScaleSideFromDom();
    }
    if (this._scaleSideComputed != null) return this._scaleSideComputed;
    if (this.mockupScale && this.mockupScale.scaleSide != null) {
      return this.mockupScale.scaleSide;
    }
    return 1;
  };

  FallRenderer.prototype._getCountdownFlaskHeightRatio = function () {
    var cfg = this.runConfig || {};
    var ratio = cfg.FALL_COUNTDOWN_FLASK_HEIGHT_RATIO != null
      ? Number(cfg.FALL_COUNTDOWN_FLASK_HEIGHT_RATIO)
      : (C.RUN_CONFIG && C.RUN_CONFIG.FALL_COUNTDOWN_FLASK_HEIGHT_RATIO != null
        ? Number(C.RUN_CONFIG.FALL_COUNTDOWN_FLASK_HEIGHT_RATIO)
        : 0.6);
    return isFinite(ratio) && ratio > 0 ? ratio : 0.6;
  };

  /** Canvas font family — quoted names match `core/game.js` (spaces break unquoted canvas fonts). */
  FallRenderer.prototype._countdownFontFamily = function () {
    return "'Fredoka', 'Baloo 2', sans-serif";
  };

  /** Peak digit size in play-column px — same flask height as `drawFlask`. */
  FallRenderer.prototype._getCountdownPeakFontSizePx = function (runHeight) {
    var scale = this.scale;
    var scaledFlaskH = scale && scale.runwayFlaskDisplayHeight != null
      ? Number(scale.runwayFlaskDisplayHeight)
      : NaN;
    if (isFinite(scaledFlaskH) && scaledFlaskH > 0) {
      var dims = this._getFlaskDims(runHeight || 600);
      var flaskH = dims && dims.flaskH > 0 ? dims.flaskH : scaledFlaskH;
      return Math.max(1, Math.round(flaskH * this._getCountdownFlaskHeightRatio()));
    }
    var cfg = this.runConfig || {};
    var basePx = cfg.FALL_COUNTDOWN_FONT_BASE_PX != null
      ? cfg.FALL_COUNTDOWN_FONT_BASE_PX
      : (C.RUN_CONFIG && C.RUN_CONFIG.FALL_COUNTDOWN_FONT_BASE_PX != null
        ? C.RUN_CONFIG.FALL_COUNTDOWN_FONT_BASE_PX
        : 72);
    return fallCountdownFontSizePx(this._getMockupScaleSide(), basePx);
  };

  /**
   * Loader warm-up (core/play-warmup.js): the inked words a Fall shows, the 3-2-1 countdown
   * digits and every callout line, baked now with the same calls, sizes and density as
   * drawCountdown / drawCallout, one job per word. Each job returns its canvas (for a GPU
   * pre-upload) or null.
   * @param {number} runWidth
   * @param {number} runHeight
   * @param {number} [dpr] playfield resolution (undefined on Canvas 2D, as the draw passes)
   * @returns {Array<() => (HTMLCanvasElement|null)>}
   */
  FallRenderer.prototype.letteringWarmJobs = function (runWidth, runHeight, dpr) {
    var peakFontPx = this._getCountdownPeakFontSizePx(runHeight);
    var calloutPx = computeFallCalloutFontPx(runWidth, peakFontPx);
    var jobs = [];
    [3, 2, 1].forEach(function (n) {
      jobs.push(function () {
        var baked = getFallLettering(String(n), COUNTDOWN_COLORS[n] || "#ffc84a", peakFontPx, { dpr: dpr });
        return baked ? baked.canvas : null;
      });
    });
    getAllFallCalloutTexts().forEach(function (text) {
      jobs.push(function () {
        var baked = getInkTextSprite(text, getFallCalloutColor(text), calloutPx, { dpr: dpr });
        return baked ? baked.canvas : null;
      });
    });
    return jobs;
  };

  /** Each new digit: a gold ring runs out from behind it and sparkles in its colour fly. */
  FallRenderer.prototype._countdownTickFx = function (juice, x, y, fontPx, color) {
    var E = juice.engine;
    E.ring(x, y, { radius: fontPx * 0.78, palette: "gold", life: 0.42, width: 1.4, start: 0.3 });
    E.sparkles(x, y, {
      count: 9, colors: [color, "#ffffff", "#fff3b8"], shape: "sparkle-ink", radius: fontPx * 0.36,
      speed: [150, 280], size: [3.6, 6.4], life: [0.35, 0.6], gravity: 180, up: 40, drag: 2.4,
    });
  };

  /**
   * drawCountdown — the 3, 2, 1 digit in chunky inked lettering (traffic-light colours), which
   * springs in tall-then-wide each second, rests and zips away; a ring and sparkles mark each tick.
   */
  FallRenderer.prototype.drawCountdown = function (ctx, runWidth, runHeight, countdownRemaining, opts) {
    if (!countdownRemaining || countdownRemaining <= 0) return;
    var displayNum = Math.ceil(countdownRemaining);
    if (displayNum <= 0) return;
    var u = displayNum - countdownRemaining;
    var pop;
    if (prefersReducedMotion()) {
      var fa = u < 0.08 ? u / 0.08 : (u > 0.85 ? 1 - (u - 0.85) / 0.15 : 1);
      pop = { sx: 1, sy: 1, alpha: clamp(fa, 0, 1) };
    } else {
      pop = countdownDigitPop(u);
    }
    var peakFontPx = this._getCountdownPeakFontSizePx(runHeight);
    var color = COUNTDOWN_COLORS[displayNum] || "#ffc84a";
    var playfield = opts && opts.playfield;
    var origin = this._worldOrigin(opts);
    var cy = runHeight * 0.38;
    var juice = this._getJuice(opts);
    if (this._countdownDigit !== displayNum) {
      this._countdownDigit = displayNum;
      if (juice && u < 0.2) this._countdownTickFx(juice, origin.x + runWidth / 2, origin.y + cy, peakFontPx, color);
    }
    var dpr = playfield && playfield.resolution > 0 ? playfield.resolution : undefined;
    var baked = getFallLettering(String(displayNum), color, peakFontPx, { dpr: dpr });
    if (baked) {
      if (playfield && typeof playfield.effect === "function") {
        playfield.effect({
          key: "countdown",
          kind: "texture",
          image: baked.canvas,
          x: origin.x + runWidth / 2,
          y: origin.y + cy,
          width: baked.w * pop.sx,
          height: baked.h * pop.sy,
          alpha: pop.alpha,
          zIndex: 50,
        });
        return;
      }
      if (!ctx || typeof ctx.drawImage !== "function") return;
      ctx.save();
      ctx.globalAlpha = pop.alpha;
      ctx.translate(runWidth / 2, cy);
      ctx.scale(pop.sx, pop.sy);
      ctx.drawImage(baked.canvas, -baked.w / 2, -baked.h / 2, baked.w, baked.h);
      ctx.restore();
      return;
    }
    // No baked lettering (tests, exotic hosts): the same digit as inked text.
    if (playfield && typeof playfield.effect === "function") {
      playfield.effect({
        key: "countdown",
        kind: "label",
        x: origin.x + runWidth / 2,
        y: origin.y + cy,
        text: String(displayNum),
        fontSize: peakFontPx,
        fontWeight: "700",
        fill: parseInt(color.slice(1), 16),
        strokeFill: 0x172a3a,
        stroke: Math.max(3, peakFontPx * 0.12),
        scaleX: pop.sx,
        scaleY: pop.sy,
        alpha: pop.alpha,
        zIndex: 50,
      });
      return;
    }
    ctx.save();
    ctx.globalAlpha = pop.alpha;
    ctx.translate(runWidth / 2, cy);
    ctx.scale(pop.sx, pop.sy);
    ctx.font = "bold " + peakFontPx + "px " + this._countdownFontFamily();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";
    ctx.lineWidth = Math.max(3, peakFontPx * 0.12);
    ctx.strokeStyle = INK;
    ctx.fillStyle = color;
    if (typeof ctx.strokeText === "function") ctx.strokeText(String(displayNum), 0, 0);
    ctx.fillText(String(displayNum), 0, 0);
    ctx.restore();
  };

  /** A callout lands: a few stars and sparkles burst round the words. */
  FallRenderer.prototype._calloutFx = function (juice, x, y, width, color) {
    var E = juice.engine;
    E.sparkles(x, y, {
      count: 12, colors: [color, "#ffffff", "#fff3b8"], shape: "mix", radius: width * 0.42,
      speed: [90, 200], size: [3.2, 5.6], life: [0.4, 0.7], gravity: 160, up: 50, drag: 2.2,
    });
    E.sparkles(x, y, {
      count: 4, shape: "star", radius: width * 0.38, speed: [80, 160], size: [4, 6.5],
      life: [0.5, 0.8], gravity: 140, up: 60, drag: 2,
    });
  };

  /**
   * One centered line at the countdown anchor: chunky inked lettering (juice-text bake) with a
   * squash-and-stretch pop in, a rest, and an anticipation squash before it zips up and away.
   * @param {{ text?: string, ageMs?: number, durationMs?: number }|null} callout
   */
  FallRenderer.prototype.drawCallout = function (ctx, runWidth, runHeight, callout, opts) {
    if (!callout || !callout.text) {
      this._calloutAge = null;
      return;
    }
    var reduced = prefersReducedMotion();
    var look = getFallCalloutPresentation(callout.ageMs, callout.durationMs, reduced);
    var text = String(callout.text);
    var age = Number(callout.ageMs) || 0;
    var isNew = this._calloutAge == null || age < this._calloutAge || this._calloutText !== text;
    this._calloutAge = age;
    this._calloutText = text;
    if (!look || look.alpha <= 0) return;
    var peakFontPx = computeFallCalloutFontPx(
      runWidth,
      this._getCountdownPeakFontSizePx(runHeight)
    );
    var color = getFallCalloutColor(text);
    var playfield = opts && opts.playfield;
    var origin = this._worldOrigin(opts);
    var y = runHeight * FALL_CALLOUT_ANCHOR_Y;
    var scaleX = look.scaleX != null ? look.scaleX : look.scale;
    var scaleY = look.scaleY != null ? look.scaleY : look.scale;
    var offsetY = look.offsetY || 0;
    var dpr = playfield && playfield.resolution > 0 ? playfield.resolution : undefined;
    var baked = getInkTextSprite(text, color, peakFontPx, { dpr: dpr });
    var juice = this._getJuice(opts);
    if (isNew && juice && !reduced && age < 160) {
      this._calloutFx(juice, origin.x + runWidth / 2, origin.y + y, baked ? baked.w : peakFontPx * text.length * 0.5, color);
    }
    if (baked) {
      if (playfield && typeof playfield.effect === "function") {
        playfield.effect({
          key: "fall-callout",
          kind: "texture",
          image: baked.canvas,
          x: origin.x + runWidth / 2,
          y: origin.y + y + offsetY,
          width: baked.w * scaleX,
          height: baked.h * scaleY,
          alpha: look.alpha,
          zIndex: 51,
        });
        return;
      }
      if (!ctx || typeof ctx.drawImage !== "function") return;
      ctx.save();
      ctx.globalAlpha = look.alpha;
      ctx.translate(runWidth / 2, y + offsetY);
      ctx.scale(scaleX, scaleY);
      ctx.drawImage(baked.canvas, -baked.w / 2, -baked.h / 2, baked.w, baked.h);
      ctx.restore();
      return;
    }
    if (playfield && typeof playfield.effect === "function") {
      playfield.effect({
        key: "fall-callout",
        kind: "label",
        x: origin.x + runWidth / 2,
        y: origin.y + y + offsetY,
        text: text,
        fontSize: peakFontPx,
        fontWeight: "700",
        fill: 0xffffff,
        strokeFill: 0x172a3a,
        stroke: computeScorePopupStrokeWidth(peakFontPx),
        scale: look.scale,
        scaleX: scaleX,
        scaleY: scaleY,
        alpha: look.alpha,
        zIndex: 51,
      });
      return;
    }
    if (!ctx || typeof ctx.save !== "function") return;
    ctx.save();
    ctx.globalAlpha = look.alpha;
    ctx.translate(runWidth / 2, y + offsetY);
    ctx.scale(scaleX, scaleY);
    ctx.font = "700 " + peakFontPx + "px " + this._countdownFontFamily();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";
    ctx.lineWidth = computeScorePopupStrokeWidth(peakFontPx);
    ctx.strokeStyle = INK;
    ctx.fillStyle = "#ffffff";
    if (typeof ctx.strokeText === "function") ctx.strokeText(text, 0, 0);
    ctx.fillText(text, 0, 0);
    ctx.restore();
  };

  /**
   * Play logic stays in runWidth×runHeight; draw clip extends so spawns, side
   * bounces, and the runway flask at column edges stay visible until the sea.
   */
  FallRenderer.prototype.getDrawClipRect = function (opts) {
    opts = opts || {};
    var runWidth = Math.max(1, Number(opts.runWidth) || 1);
    var runHeight = Math.max(1, Number(opts.runHeight) || 1);
    var cfg = this.runConfig || {};
    var topOverflow = opts.clipTopOverflow != null
      ? Math.max(0, Number(opts.clipTopOverflow))
      : computeFallDrawTopOverflowPx(runWidth, cfg, runHeight);
    var bottomLocal = opts.clipBottomLocal != null && isFinite(Number(opts.clipBottomLocal))
      ? Math.max(runHeight, Number(opts.clipBottomLocal))
      : runHeight;
    var clipX = 0;
    var clipW = runWidth;
    var originX = opts.clipOriginX != null ? Number(opts.clipOriginX) : 0;
    var canvasW = opts.clipCanvasWidth != null ? Number(opts.clipCanvasWidth) : 0;
    if (isFinite(canvasW) && canvasW > 0) {
      clipX = -Math.max(0, originX);
      clipW = canvasW;
    } else {
      var scale = opts.scale || this.scale;
      var flaskW = scale && scale.runwayFlaskDisplayWidth ? scale.runwayFlaskDisplayWidth : 120;
      var sidePad = Math.max(flaskW, runWidth * 0.35);
      clipX = -sidePad;
      clipW = runWidth + sidePad * 2;
    }
    return {
      x: clipX,
      y: -topOverflow,
      width: clipW,
      height: bottomLocal + topOverflow,
    };
  };

  /**
   * draw — full frame render. Called each game loop step.
   */
  FallRenderer.prototype.draw = function (ctx, opts) {
    if (opts.scale) this.scale = opts.scale;
    var runWidth = opts.runWidth;
    var runHeight = opts.runHeight;
    var bodyEntries = opts.bodyEntries || [];
    var player = opts.player;
    var currentTime = opts.currentTime || 0;
    var state = opts.state || "playing";
    var countdownRemaining = opts.countdownRemaining || 0;
    ctx.save();
    var clip = this.getDrawClipRect(opts);
    // Pixi clips the world with a mask (setWorldClip below), and `ctx` is then the 2D fx
    // overlay: clipping it as well marked that full-screen layer dirty and re-uploaded it as a
    // texture every Fall frame although nothing else draws into it.
    if (!opts.playfield) {
      ctx.beginPath();
      ctx.rect(clip.x, clip.y, clip.width, clip.height);
      ctx.clip();
    }

    var catcherIntroAlpha = opts.catcherFlaskIntroAlpha != null ? opts.catcherFlaskIntroAlpha : 1;
    if (opts.playfield) {
      var origin = this._worldOrigin(opts);
      opts.playfield.setWorldClip(
        origin.x + clip.x,
        origin.y + clip.y,
        clip.width,
        clip.height
      );
    }
    if (opts.showMovementRange) {
      this.drawMovementRange(ctx, {
        showMovementRange: true,
        runWidth: runWidth,
        runHeight: runHeight,
        player: player,
        playerX: player ? player.x : runWidth / 2,
        scale: opts.scale || this.scale,
        currentTime: currentTime,
        animTime: opts.animTime,
        catcherFlaskIntroAlpha: catcherIntroAlpha,
        playfield: opts.playfield,
        worldOrigin: opts.worldOrigin,
        fallPhysics: opts.fallPhysics,
      });
    }
    if (player) {
      this.drawSwallowed(ctx, bodyEntries, player, runWidth, runHeight, catcherIntroAlpha, opts);
      this.drawFlask(ctx, player, runWidth, runHeight, currentTime, catcherIntroAlpha, opts);
    }
    if (opts.fallPhysics) {
      drawFallColliderDebug(ctx, opts.fallPhysics, { bodyEntries: bodyEntries });
    }
    this.drawObjects(ctx, bodyEntries, runWidth, runHeight, currentTime, opts);
    if (player) {
      this.drawShieldGlow(
        ctx,
        player,
        runWidth,
        runHeight,
        opts.animTime != null ? opts.animTime : currentTime,
        opts
      );
    }
    if (opts.flaskShardBursts && opts.flaskShardBursts.length && FlaskRenderer.drawFlaskShardBursts) {
      FlaskRenderer.drawFlaskShardBursts(
        ctx,
        opts.flaskShardBursts,
        opts.animTime != null ? opts.animTime : currentTime,
        {
          gravityPx: opts.flaskShardGravityPx,
          viewport: opts.flaskShardViewport,
          playfield: opts.playfield,
          worldOrigin: opts.worldOrigin,
        }
      );
    }

    var cfg = this.runConfig || {};
    var silentCountdown = !!cfg.fallCountdownSilent
      || opts.fallCountdownSilent === true;
    if (state === "countdown" && countdownRemaining > 0 && !silentCountdown) {
      this.drawCountdown(ctx, runWidth, runHeight, countdownRemaining, opts);
    } else {
      this._countdownDigit = null;
    }
    if (opts.fallCallout) {
      this.drawCallout(ctx, runWidth, runHeight, opts.fallCallout, opts);
    } else {
      this._calloutAge = null;
    }

    ctx.restore();
  };
